import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { AppointmentData, ClientData, ProfessionalData, IncomingMessageNotification, ClientPack, CommunicationStatusUpdate, CommunicationData } from '../models';
import { AiScriberReportDraft } from '../store/aiScriber/state';

// SIGNALR WEBSOCKET SERVICE

export class WebSocketService {
    private connection: HubConnection | null = null;
    private keepAliveTimer: number | null = null;
    private callbacks: {
        onAppointmentCreated?: (appointment: AppointmentData) => void;
        onAppointmentUpdated?: (appointment: AppointmentData | string) => void;
        onAppointmentDeleted?: (appointmentId: string) => void;
        onClientCreated?: (client: ClientData) => void;
        onClientUpdated?: (client: ClientData) => void;
        onClientDeleted?: (clientId: string) => void;
        onProfessionalCreated?: (professional: ProfessionalData) => void;
        onProfessionalUpdated?: (professional: ProfessionalData) => void;
        onProfessionalDeleted?: (professionalId: string) => void;
        onIncomingCommunicationReceived?: (notification: IncomingMessageNotification) => void;
        onCommunicationMarkedAsRead?: (communicationId: string, read: boolean) => void;
        onAllCommunicationsMarkedAsRead?: () => void;
        onCommunicationUpdated?: (communication: CommunicationData) => void;
        onCommunicationStatusUpdated?: (update: CommunicationStatusUpdate) => void;
        onPackUpdated?: (appointment: ClientPack | string) => void;
        onNotificationsUpdated?: () => void;
        onTasksUpdated?: () => void;
        onAiScriberTranscriptDelta?: (sessionId: string, delta: string) => void;
        onAiScriberReportDraftGenerated?: (sessionId: string, reportDraft: AiScriberReportDraft | null, templateId: string) => void;
    } = {};
    private isConnecting = false;
    private readonly keepAliveIntervalMs = 20000; // Send ping every 20s (backend keep-alive is 30s)

    constructor() {
        this.initializeConnection();
    }

    private initializeConnection(): void {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
        this.connection = new HubConnectionBuilder()
            .withUrl(`${apiBaseUrl}/events-hub`, {
                // Ensure credentials (cookies) are sent with WebSocket requests
                withCredentials: true
            })
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .configureLogging(LogLevel.Information)
            .build();

        // Align with backend settings: ClientTimeoutInterval=2min, KeepAliveInterval=30s
        this.connection.serverTimeoutInMilliseconds = 120000; // 2 minutes (matches backend ClientTimeoutInterval)
        this.connection.keepAliveIntervalInMilliseconds = 30000; // 30 seconds (matches backend KeepAliveInterval)

        this.setupEventHandlers();
        this.setupConnectionHandlers();
    }

    private setupConnectionHandlers(): void {
        if (!this.connection) return;

        this.connection.onreconnecting((error) => {
            console.warn('SignalR connection lost. Attempting to reconnect...', error);
            this.stopKeepAlive();
        });

        this.connection.onreconnected((connectionId) => {
            console.log(`SignalR connection re-established. Connection ID: ${connectionId}`);
            this.startKeepAlive();
        });

        this.connection.onclose((error) => {
            console.error('SignalR connection closed:', error);
            this.isConnecting = false;
            this.stopKeepAlive();
        });
    }

    private setupEventHandlers(): void {
        if (!this.connection) return;

        this.connection.on('AppointmentCreated', (appointment: AppointmentData) => {
            this.callbacks.onAppointmentCreated?.(appointment);
        });

        this.connection.on('AppointmentUpdated', (appointment: AppointmentData) => {
            this.callbacks.onAppointmentUpdated?.(appointment);
        });

        this.connection.on('AppointmentDeleted', (appointmentId: string) => {
            this.callbacks.onAppointmentDeleted?.(appointmentId);
        });

        this.connection.on('ClientCreated', (client: ClientData) => {
            this.callbacks.onClientCreated?.(client);
        });

        this.connection.on('ClientUpdated', (client: ClientData) => {
            this.callbacks.onClientUpdated?.(client);
        });

        this.connection.on('ClientDeleted', (clientId: string) => {
            this.callbacks.onClientDeleted?.(clientId);
        });

        this.connection.on('ProfessionalCreated', (professional: ProfessionalData) => {
            this.callbacks.onProfessionalCreated?.(professional);
        });

        this.connection.on('ProfessionalUpdated', (professional: ProfessionalData) => {
            this.callbacks.onProfessionalUpdated?.(professional);
        });

        this.connection.on('ProfessionalDeleted', (professionalId: string) => {
            this.callbacks.onProfessionalDeleted?.(professionalId);
        });

        this.connection.on('IncomingCommunicationReceived', (notification: IncomingMessageNotification) => {
            this.callbacks.onIncomingCommunicationReceived?.(notification);
        });

        this.connection.on('CommunicationMarkedAsRead', (communicationId: string, read: boolean) => {
            this.callbacks.onCommunicationMarkedAsRead?.(communicationId, read);
        });

        this.connection.on('AllCommunicationsMarkedAsRead', () => {
            this.callbacks.onAllCommunicationsMarkedAsRead?.();
        });

        this.connection.on('CommunicationUpdated', (communication: CommunicationData) => {
            this.callbacks.onCommunicationUpdated?.(communication);
        });

        this.connection.on('CommunicationStatusUpdated', (update: CommunicationStatusUpdate) => {
            this.callbacks.onCommunicationStatusUpdated?.(update);
        });

        this.connection.on('PackUpdated', (pack: ClientPack) => {
            this.callbacks.onPackUpdated?.(pack);
        });

        this.connection.on('NotificationsUpdated', () => {
            this.callbacks.onNotificationsUpdated?.();
        });

        this.connection.on('TasksUpdated', () => {
            this.callbacks.onTasksUpdated?.();
        });

        this.connection.on('aiScriber.transcriptDelta', (payload: {
            sessionId: string;
            text: string;
            timestampUtc: string;
        }) => {
            this.callbacks.onAiScriberTranscriptDelta?.(payload.sessionId, payload.text);
        });

        this.connection.on('aiScriber.reportDraftGenerated', (payload: {
            sessionId: string;
            reportDraft: AiScriberReportDraft | null;
            templateId: string;
        }) => {
            this.callbacks.onAiScriberReportDraftGenerated?.(payload.sessionId, payload.reportDraft, payload.templateId);
        });
    }

    public async start(): Promise<void> {
        if (!this.connection) {
            this.initializeConnection();
        }

        if (!this.connection || this.isConnecting) {
            return;
        }

        if (this.connection.state === HubConnectionState.Connected) {
            console.log('SignalR already connected');
            return;
        }

        try {
            this.isConnecting = true;
            await this.connection.start();
            this.startKeepAlive();
            console.log(`SignalR connected. Connection ID: ${this.connection.connectionId}`);
            this.isConnecting = false;
        } catch (error) {
            console.error('SignalR connection failed:', error);
            this.isConnecting = false;
            throw error;
        }
    }

    public async stop(): Promise<void> {
        if (!this.connection) return;

        if (this.connection.state === HubConnectionState.Disconnected) {
            console.log('SignalR already disconnected');
            return;
        }

        try {
            this.stopKeepAlive();
            await this.connection.stop();
            console.log('SignalR connection closed');
        } catch (error) {
            console.error('Error stopping SignalR connection:', error);
        }
    }

    public onAppointmentCreated(callback: (appointment: AppointmentData) => void): void {
        this.callbacks.onAppointmentCreated = callback;
    }

    public onAppointmentUpdated(callback: (appointment: AppointmentData | string) => void): void {
        this.callbacks.onAppointmentUpdated = callback;
    }

    public onAppointmentDeleted(callback: (appointmentId: string) => void): void {
        this.callbacks.onAppointmentDeleted = callback;
    }

    public onClientCreated(callback: (client: ClientData) => void): void {
        this.callbacks.onClientCreated = callback;
    }

    public onClientUpdated(callback: (client: ClientData) => void): void {
        this.callbacks.onClientUpdated = callback;
    }

    public onClientDeleted(callback: (clientId: string) => void): void {
        this.callbacks.onClientDeleted = callback;
    }

    public onProfessionalCreated(callback: (professional: ProfessionalData) => void): void {
        this.callbacks.onProfessionalCreated = callback;
    }

    public onProfessionalUpdated(callback: (professional: ProfessionalData) => void): void {
        this.callbacks.onProfessionalUpdated = callback;
    }

    public onProfessionalDeleted(callback: (professionalId: string) => void): void {
        this.callbacks.onProfessionalDeleted = callback;
    }

    public onIncomingCommunicationReceived(callback: (notification: IncomingMessageNotification) => void): void {
        this.callbacks.onIncomingCommunicationReceived = callback;
    }

    public onCommunicationMarkedAsRead(callback: (communicationId: string, read: boolean) => void): void {
        this.callbacks.onCommunicationMarkedAsRead = callback;
    }

    public onAllCommunicationsMarkedAsRead(callback: () => void): void {
        this.callbacks.onAllCommunicationsMarkedAsRead = callback;
    }

    public onCommunicationUpdated(callback: (communication: CommunicationData) => void): void {
        this.callbacks.onCommunicationUpdated = callback;
    }

    public onCommunicationStatusUpdated(callback: (update: CommunicationStatusUpdate) => void): void {
        this.callbacks.onCommunicationStatusUpdated = callback;
    }

    public onPackUpdated(callback: (pack: ClientPack | string) => void): void {
        this.callbacks.onPackUpdated = callback;
    }

    public onNotificationsUpdated(callback: () => void): void {
        this.callbacks.onNotificationsUpdated = callback;
    }

    public onTasksUpdated(callback: () => void): void {
        this.callbacks.onTasksUpdated = callback;
    }

    public onAiScriberTranscriptDelta(callback: (sessionId: string, delta: string) => void): void {
        this.callbacks.onAiScriberTranscriptDelta = callback;
    }

    public onAiScriberReportDraftGenerated(callback: (sessionId: string, reportDraft: AiScriberReportDraft | null, templateId: string) => void): void {
        this.callbacks.onAiScriberReportDraftGenerated = callback;
    }

    public getConnectionState(): string {
        return this.connection?.state || 'Disconnected';
    }

    public isConnected(): boolean {
        return this.connection?.state === HubConnectionState.Connected;
    }

    private startKeepAlive(): void {
        if (this.keepAliveTimer || !this.connection) return;

        this.keepAliveTimer = window.setInterval(async () => {
            if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
                return;
            }

            try {
                await this.connection.invoke('Ping');
            } catch (error) {
                console.error('SignalR keep-alive ping failed:', error);
            }
        }, this.keepAliveIntervalMs);
    }

    private stopKeepAlive(): void {
        if (!this.keepAliveTimer) return;
        window.clearInterval(this.keepAliveTimer);
        this.keepAliveTimer = null;
    }
}

export const websocketService = new WebSocketService();
